import { SelectOption } from '@c/forms/controls/FormSelect';
import { ListIcon, GridIcon } from '@c/icons';
import { useMutation } from '@tanstack/react-query';
import { ButtonToggleType } from '@util/types/buttontoggle';
import { ProductDocument } from '@util/types/firestore/products';
import { CategoryDocument } from '@util/firestore/categories';
import { Pagination } from '@util/types/paginator';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import isEqual from 'deep-eql';
import { getProductsPageDefaults, readProductsPageUrl, writeProductsPageUrl, ProductsPageUrlState } from './productsPageUrl';
import { getProducts } from '@util/firestore/products';
import {
  ProductLimitOption,
  productLimitOptions,
  ProductSortOption,
  productSortOptions,
} from '@util/firestore/products';
import { CategoryOrSubcategoryPageProps } from '@c/CategoryOrSubcategoryPage';
import { logError } from '@util/logError';

export type PageType =
  | 'category'
  | 'featured'
  | 'brand'
  | 'search'
  | 'all'
  | 'onsale'
  | 'staff-picks'
  | 'deals-and-steals'
  | 'new-arrivals'
  | 'vintage-camera-gear'
  | 'auctions';

export type ProductView = 'list' | 'grid';

export type ListingsFilters = {
  minPrice: number | null;
  maxPrice: number | null;
  category: CategoryDocument | null;
  condition: string[];
  brand: string[];
  deals: string[];
  useCases: string[];
  additional?: {
    [key: string]: string | string[];
  };
};

export type PaginationFilters = {
  sort: ProductSortOption;
  limit: ProductLimitOption;
  view: ProductView;
};

const useProductsPage = ({
  results,
  initialTopFilters,
  initialSideFilters,
  pageInfo: initialPageInfo,
  curated_list,
  categoryOptions,
  conditionOptions,
  brandOptions,
  keyword,
  seller_id,
  useClient,
}: CategoryOrSubcategoryPageProps) => {
  const router = useRouter();
  const initialState = {
    topFilters: initialTopFilters,
    sideFilters: {
      ...initialSideFilters,
      brand: typeof initialSideFilters.brand === 'string' ? [initialSideFilters.brand] : initialSideFilters.brand,
      condition: typeof initialSideFilters.condition === 'string' ? [initialSideFilters.condition] : initialSideFilters.condition,
      deals: typeof initialSideFilters.deals === 'string' ? [initialSideFilters.deals] : initialSideFilters.deals,
      useCases: typeof initialSideFilters.useCases === 'string' ? [initialSideFilters.useCases] : initialSideFilters.useCases,
      additional: initialSideFilters.additional ?? {},
    },
    page: initialPageInfo.page,
  };
  const defaults = getProductsPageDefaults(router.asPath, initialState);
  const liveState = useRef<ProductsPageUrlState>(initialState);
  const latestRequest = useRef(0);
  const restoredResults = useRef<ProductDocument[] | null>(null);
  const restorationSeed = { state: initialState, path: router.asPath.split(/[?#]/)[0] };
  const restoredSeed = useRef<typeof restorationSeed | null>(null);

  // Filters
  const [topFilters, setTopFilters] = useState<PaginationFilters>(initialTopFilters);
  const [sideFilters, setSideFilters] =
    useState<ListingsFilters>(initialState.sideFilters);

  // Pagination
  const [pageInfo, setPageInfo] = useState<Pagination>(initialPageInfo);

  // Product Fetching
  const [isFetching, setIsFetching] = useState(false);
  const [products, setProducts] = useState<ProductDocument[]>(results);

  // List / Grid view
  const [view, setView] = useState<ProductView>(topFilters.view ?? 'list');
  const buttonToggles: ButtonToggleType[] = [
    {
      key: 'list',
      icon: <ListIcon />,
      onClick: () => {
        changeView('list');
      },
      selected: view === 'list',
    },
    {
      key: 'grid',
      icon: <GridIcon />,
      onClick: () => {
        changeView('grid');
      },
      selected: view === 'grid',
    },
  ];

  const getProductsMutation = useMutation({ mutationFn: getProducts });

  function writeUrl(state: ProductsPageUrlState) {
    liveState.current = state;
    const nextUrl = writeProductsPageUrl(router.asPath, state, defaults);
    // Replace the actual path, not router.query: /brands/[brand] uses the
    // same name for its route segment and its filter query parameter.
    void router.replace(nextUrl, undefined, { shallow: true, scroll: false }).catch((error) => {
      // Next cancels an earlier shallow transition when the shopper changes
      // another filter quickly; the latest replacement is the intended URL.
      if (!error?.cancelled) logError(error);
    });
  }

  function changeView(nextView: ProductView) {
    const next = { ...liveState.current, topFilters: { ...liveState.current.topFilters, view: nextView } };
    setTopFilters(next.topFilters);
    setView(nextView);
    writeUrl(next);
  }

  function fetchProducts(
    tFilters: PaginationFilters,
    sFilters: ListingsFilters,
    page: number
  ) {
    const requestId = ++latestRequest.current;
    if (!useClient) {
      // Keep loading synchronous with the selection. React Query starts on
      // a later microtask; waiting for it lets the empty-search detector see
      // old results under the new filters (#1401 filterClearRace regression).
      setIsFetching(true);
    }

    if (useClient) {
      // filter products on client
      const filteredProducts = results.filter((product) => {
        // side filters
        //if (sFilters.onsale && !product.on_sale) return false;
        if (sFilters.minPrice && product.price < sFilters.minPrice)
          return false;
        if (sFilters.maxPrice && product.price > sFilters.maxPrice)
          return false;
        if (sFilters.condition) {
          if (
            !sFilters.condition.includes(
              product.condition as string
            )
          )
            return false;
        }
        if (sFilters.brand) {
          if (
            !sFilters.brand.includes(
              product.brand as string
            )
          ) return false;  
        }

        if (sFilters.category) {
          const matches = product.categories.includes(sFilters.category.name)
          if (!matches) return false;
        }

        return true;
      });

      // sort by top filters
      const sortedFilteredResults = filteredProducts.sort((a, b) => {
        switch (tFilters.sort) {
          case 'priceAsc':
            return a.price - b.price;
          case 'priceDesc':
            return b.price - a.price;
          case 'newest':
            return b.date_added - a.date_added;
          case 'ranked':
            return b.rank_score - a.rank_score;
          default:
            return 0;
        }
      });
      setProducts(sortedFilteredResults);
      return;
    }

    void getProductsMutation.mutateAsync({
      keyword,
      last_slug: null,
      seller_id,
      is_expired: false,
      is_verified: true,
      is_draft: false,
      source: 'web',
      curated_list,
      page,
      additionalFilters: sFilters.additional ?? {},
      deals: sFilters.deals,
      useCases: sFilters.useCases,
      minPrice: sFilters.minPrice,
      maxPrice: sFilters.maxPrice,
      limit: tFilters.limit,
      sort: tFilters.sort,
      conditionFilters: sFilters.condition,
      conditionOptions: conditionOptions,
      brandOptions: brandOptions,
      brandFilters: sFilters.brand,
      categoryOptions: categoryOptions,
      categoryFilters: sFilters.category,
    }).then((data) => {
      // A slower previous filter request must not replace the result list
      // belonging to the newer URL selection.
      if (requestId !== latestRequest.current) return;
      setIsFetching(false);
      setProducts(data.results);
      setPageInfo(data.pageInfo);
    }).catch((error) => {
      if (requestId !== latestRequest.current) return;
      setIsFetching(false);
      logError(error);
    });
  }

  // Shallow navigation keeps Next's original SSR props in its page cache.
  // On Back, read the URL again and fetch only if those props describe an
  // older selection. A normal SSR visit keeps its existing results.
  useEffect(() => {
    // A caller may recreate the same array on every render. Skip that repeat,
    // but still restore when new server filters, page, or route arrive.
    if (!router.isReady || (isEqual(restoredResults.current, results) && isEqual(restoredSeed.current, restorationSeed))) return;
    restoredResults.current = results;
    restoredSeed.current = restorationSeed;
    latestRequest.current += 1;
    setIsFetching(false);
    const restored = readProductsPageUrl(router.asPath, defaults, categoryOptions);
    liveState.current = restored;
    setTopFilters(restored.topFilters);
    setSideFilters(restored.sideFilters);
    setView(restored.topFilters.view);
    setPageInfo({ ...initialPageInfo, page: restored.page });
    setProducts(results);
    const restoredDataState = { ...restored, topFilters: { ...restored.topFilters, view: initialState.topFilters.view } };
    if (!isEqual(restoredDataState, initialState)) {
      fetchProducts(restored.topFilters, restored.sideFilters, restored.page);
    }
    // Only a new SSR result set (or the router becoming ready) restores state;
    // our own shallow replacements must not undo an in-flight selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, results]);

  useEffect(() => () => {
    latestRequest.current += 1;
    restoredResults.current = null;
    restoredSeed.current = null;
  }, []);

  // Filter change handlers use a synchronous ref so rapid interactions don't
  // build a new URL from React state captured before the previous click.
  const handleSideFiltersChange = (filters: ListingsFilters) => {
    const next = { ...liveState.current, sideFilters: filters, page: 1 };
    setSideFilters(filters);
    setPageInfo(prev => ({ ...prev, page: 1 }));
    writeUrl(next);
    fetchProducts(next.topFilters, filters, 1);
  };

  const handleLimitChange = (option: SelectOption) => {
    try {
      const limit = productLimitOptions.parse(Number(option.value));
      const next = { ...liveState.current, topFilters: { ...liveState.current.topFilters, limit } };
      setTopFilters(next.topFilters);
      writeUrl(next);
      fetchProducts(next.topFilters, next.sideFilters, next.page);
    } catch (e) {
      logError(e);
    }
  };

  const handleSortChange = (option: SelectOption) => {
    try {
      const sort = productSortOptions.parse(option.value);
      const next = { ...liveState.current, topFilters: { ...liveState.current.topFilters, sort } };
      setTopFilters(next.topFilters);
      writeUrl(next);
      fetchProducts(next.topFilters, next.sideFilters, next.page);
    } catch (e) {
      logError(e);
    }
  };

  const handlePageChange = (page: number) => {
    const next = { ...liveState.current, page };
    setPageInfo(prev => ({ ...prev, page }));
    writeUrl(next);
    fetchProducts(next.topFilters, next.sideFilters, page);
  };

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-open filter drawer on desktop for the FIRST category/collection/products
  // page visit in a session. Uses sessionStorage so it only fires once — returning
  // to another category page in the same session won't re-open the drawer.
  useEffect(() => {
    const path = window.location.pathname;
    const isFilterPage = path.startsWith('/c/') || path.startsWith('/collections/') || path.startsWith('/products');
    const alreadyShown = sessionStorage.getItem('gf_filters_shown');
    if (isFilterPage && window.innerWidth >= 640 && !drawerOpen && !alreadyShown) {
      setDrawerOpen(true);
      sessionStorage.setItem('gf_filters_shown', '1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  return {
    view,
    buttonToggles,
    isFetching,
    products,
    sideFilters,
    topFilters,
    pageInfo,
    handleLimitChange,
    handleSideFiltersChange,
    handleSortChange,
    handlePageChange,
    drawerOpen,
    setDrawerOpen,
  };
};

export default useProductsPage;
